import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  ReplaySubject,
  Subject,
  Subscription,
  firstValueFrom,
  of,
} from 'rxjs';
import {
  bufferTime,
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { SystemFacade } from 'src/app/shared/system/system.facade';
import { TenantConfig } from 'src/app/shared/system/system.reducer';
import { IMOBILIARIO_KEY } from 'src/app/shared/types';
import { environment } from '../../../../environments/environment';
import { Databases, postgrestClient } from '../../../shared/postgrest';
import { AuthFacade } from '../../store/auth/auth.facade';
import { PublicTokensState } from '../../store/auth/auth.reducer';
import { ProjectsFacade } from '../../store/projects/projects.facade';
import {
  GeometryForm,
  GeometryFormValues,
  ServerGeo,
} from '../../types/geometry.type';
import { Camada } from '../../types/camada.types';
import { wktToFeature } from './../../../modules/map/utils/map.utils';
import { FieldTypeFrontInt } from './../../types/formulario.types';

export interface HttpProgressReturn {
  status: string;
  progress: number;
  fileId: number;
  body?: any;
}

@Injectable({ providedIn: 'root' })
export class LayersService {
  // update layer by id
  private updateMapLayerSource$ = new Subject<number>();
  updateMapLayer$ = this.updateMapLayerSource$.asObservable();

  private comercialFactorSource$ = new Subject<number>();
  comercialFactor$ = this.comercialFactorSource$.asObservable();

  private centroidLoteWKBSource$ = new BehaviorSubject<
    string | ArrayBuffer | null
  >(null);
  centroidLoteWKB$ = this.centroidLoteWKBSource$.asObservable();

  private layerByIdRequests$ = new Subject<number>();
  private layerByIdBatching?: Subscription;
  private layerByIdPending = new Map<number, ReplaySubject<any>>();
  private layerByIdCache = new Map<number, any>();

  // Criciúma: o lote de pedidos abre só quando chega um id e fecha 20 ms depois.
  // O bufferTime(20) dos outros municípios dispara um timer a cada 20 ms mesmo sem pedidos,
  // e cada disparo faz o Angular verificar a tela inteira (mapa e formulários).
  private isCriciuma = false;
  private queuedLayerIds: number[] = [];
  private icadApiAddr?: string;

  constructor(
    private http: HttpClient,
    private projectsFacade: ProjectsFacade,
    private authFacade: AuthFacade,
    private systemFacade: SystemFacade
  ) {
    this.systemFacade.isCriciuma$.subscribe((isCriciuma) => {
      this.isCriciuma = isCriciuma;
    });
  }

  private startLayerByIdBatching() {
    this.layerByIdBatching = this.layerByIdRequests$
      .pipe(
        bufferTime(20),
        map((ids) => Array.from(new Set(ids))),
        map((ids) => ids.filter((id) => !this.layerByIdCache.has(id))),
        filter((ids) => ids.length > 0),
        switchMap((ids) =>
          this.getLayersByIds(ids).pipe(
            map((layers) => ({ ids, layers })),
            catchError((error) => {
              ids.forEach((id) => this.failLayerRequest(id, error));
              return EMPTY;
            })
          )
        )
      )
      .subscribe(({ ids, layers }) => this.resolveLayerBatch(ids, layers));
  }

  private queueLayerRequest(id: number) {
    this.queuedLayerIds.push(id);
    if (this.queuedLayerIds.length > 1) {
      return;
    }

    setTimeout(() => {
      const ids = Array.from(new Set(this.queuedLayerIds)).filter(
        (layerId) => !this.layerByIdCache.has(layerId)
      );
      this.queuedLayerIds = [];
      if (!ids.length) {
        return;
      }

      // Cada lote tem a sua requisição: um lote novo não cancela o anterior
      // (com switchMap, os ids do lote cancelado ficavam pendentes para sempre).
      this.getLayersByIds(ids).subscribe({
        next: (layers) => this.resolveLayerBatch(ids, layers),
        error: (error) =>
          ids.forEach((layerId) => this.failLayerRequest(layerId, error)),
      });
    }, 20);
  }

  private resolveLayerBatch(ids: number[], layers: any) {
    const list = Array.isArray(layers) ? layers : [];
    const byId = new Map<number, any>();
    list.forEach((layer) => {
      if (layer?.id != null) {
        byId.set(layer.id, layer);
      }
    });

    ids.forEach((id) => {
      const layer = byId.get(id);
      this.resolveLayerRequest(id, layer);
    });
  }

  onUpdateMapLayer(layerId: number) {
    this.updateMapLayerSource$.next(layerId);
  }

  getAllLayersExtent(projectId: number | null) {
    return this.http.get<string>(
      `${environment.apiUrl}projetos/${projectId}/extent`
    );
  }

  getTest() {
    return this.http.get(
      'https://api2.webgeo.io/geometrias/camada/3402?bbox=-49.360198974609375,-28.89548411948549,-49.1550636291504,-28.75847974736891'
    );
  }

  getAllFeaturesFromLayer(layerId: number) {
    // TODO ICAD
    // if (layerId === 3402) {
    //   return of([
    //     JSON.parse(
    //       '{"id":6610975,"geomText":"POLYGON ((-49.24160922550691 -28.839272300618326, -49.24177090954971 -28.83940021438871, -49.24177519437501 -28.839396014582327, -49.241879284226115 -28.83929840472488, -49.241714216651296 -28.839169241377512, -49.24160922550691 -28.839272300618326))","seq":9701,"valores":[{"id":58340041,"projetoCamadaFormularioCampo":"48797e96-3481-43e3-88de-d864c4691061","campoView":{"id":16705,"nome":"REDUZIDO","tipo":3},"valorNum":4956,"valoresSelect":[]},{"id":58340036,"projetoCamadaFormularioCampo":"4808dce6-3818-4362-a937-00602cc32acb","campoView":{"id":16700,"nome":"INFRAESTRU","tipo":1},"valorText":"","valoresSelect":[]},{"id":58340033,"projetoCamadaFormularioCampo":"3c2e3f41-fc6a-4c35-a07a-2c2e53075e20","campoView":{"id":16697,"nome":"NUM_LOTE","tipo":1},"valorText":"0013","valoresSelect":[]},{"id":58340038,"projetoCamadaFormularioCampo":"3decd2a6-5774-4a63-9990-9e6ad634f5e6","campoView":{"id":16702,"nome":"DISTRITO","tipo":3},"valorNum":2,"valoresSelect":[]},{"id":58340032,"projetoCamadaFormularioCampo":"859ff2fe-45af-49ea-8ffc-705cd4bf73c9","campoView":{"id":16696,"nome":"NIVEL_INCO","tipo":3},"valorNum":1,"valoresSelect":[]},{"id":58340031,"projetoCamadaFormularioCampo":"1476b809-6d99-4b5c-acb7-3fed5ca9daec","campoView":{"id":16695,"nome":"id_lote","tipo":1},"valorText":"02.47.111.13","valoresSelect":[]},{"id":58340035,"projetoCamadaFormularioCampo":"0e941399-c5da-4c15-810f-54b49cc9d657","campoView":{"id":16699,"nome":"AREA_MARIN","tipo":1},"valorText":"","valoresSelect":[]},{"id":58340040,"projetoCamadaFormularioCampo":"6853a22f-f82e-47a0-946c-b74eb0880fc0","campoView":{"id":16704,"nome":"SECAO","tipo":1},"valorText":"","valoresSelect":[]},{"id":58340037,"projetoCamadaFormularioCampo":"b2fc8b71-7aba-4629-9a53-e34dc51f9fad","campoView":{"id":16701,"nome":"DISTANCIA_","tipo":3},"valorNum":74.92,"valoresSelect":[]},{"id":58340034,"projetoCamadaFormularioCampo":"f7cf3af1-1a7b-4cb7-8481-ce9b792b3c63","campoView":{"id":16698,"nome":"INSCRIC_01","tipo":1},"valorText":"02.47.111.13.001","valoresSelect":[]},{"id":58340030,"projetoCamadaFormularioCampo":"19a4a4ea-c60a-4bd6-b4b7-7d746ae01a25","campoView":{"id":16694,"nome":"AREA","tipo":3},"valorNum":329.205,"valoresSelect":[]},{"id":58340039,"projetoCamadaFormularioCampo":"3b00dd56-394e-4dae-a177-6b05187831f5","campoView":{"id":16703,"nome":"SETOR","tipo":3},"valorNum":47,"valoresSelect":[]}]}'
    //     ),
    //   ]);
    // }

    return this.http.get(`${environment.apiUrl}valores/camada/${layerId}`);
  }

  getImobiliatioKey(tenantConfig: TenantConfig) {
    const tenant = tenantConfig?.cidade as string;
    return (
      tenantConfig?.tabela_unidade ?? IMOBILIARIO_KEY[tenant] ?? 'imobiliario'
    );
  }

  getLayerByBbox(layerId: number, bbox: string) {
    return this.http
      .get(`${environment.apiUrl}valores/camada/${layerId}?bbox=${bbox}`)
      .pipe(
        map((res: any[]) => {
          const features = res.map((feature) => {
            const geometry = wktToFeature(feature.geomText);
            const properties = (feature.valores as any[]).reduce(
              (prev, curr) => {
                let value;
                /* Tipo do campo ... */
                if (curr.campoView.tipo === FieldTypeFrontInt.NUM) {
                  value = {
                    [curr.campoView.nome]: curr.valorNum,
                  };
                } else if (curr.campoView.tipo === FieldTypeFrontInt.INT) {
                  value = {
                    [curr.campoView.nome]: curr.valorInt,
                  };
                } else if (curr.campoView.tipo === FieldTypeFrontInt.TEXT) {
                  value = {
                    [curr.campoView.nome]: curr.valorText,
                  };
                } else if (curr.campoView.tipo === FieldTypeFrontInt.SELECT) {
                  value = {
                    [curr.campoView.nome]: curr.valoresSelect,
                  };
                }

                return {
                  ...prev,
                  ...value,
                  id: feature.id,
                };
              },
              {}
            );

            return {
              id: feature.id,
              type: 'Feature',
              geometry,
              properties,
            };
          });

          return { features, type: 'FeatureCollection' };
        }),
        take(1)
      )
      .toPromise();
  }

  getGeoJSONwithValues(layerId: number) {
    return this.http.get(
      `${environment.apiUrl}ouv/camadas/${layerId}/geometrias`
    );
  }

  getGeometryValues(id: number) {
    return this.http.get<GeometryFormValues[]>(
      `${environment.apiUrl}valores/geometria/${id}`
    );
  }

  getDefaultGeometryValues(id: number, page: number = 1) {
    return this.http.get<GeometryFormValues[]>(
      `${environment.apiDjangoUrl}municipio/lote/busca_imoveis/${id}/?page=${page}`
    );
  }

  getDefaultGeometryValuesByGateway(id: number, page: number = 1) {
    return this.http
      .get<GeometryFormValues[]>(
        `${environment.apiGatewayUrl}${
          (environment as any).apiGatewayRestPrefix ?? 'rest/'
        }rpc/portal_info_lote?v_id_lote=${id}&page=${page}`
      )
      .pipe(
        catchError((error) => {
          if (error?.error?.code === 'PGRST202') {
            return this.getDefaultGeometryValues(id, page);
          }
          throw error;
        })
      );
  }

  getDocumentos(id: number, nomeCamada: string = 'lote') {
    const encoded = encodeURIComponent(nomeCamada);
    return this.http.get<GeometryFormValues[]>(
      `${environment.apiDjangoUrl}municipio/midia/search2/params/?id_origem__exact=${id}&nome_camada__exact=${encoded}`
    );
  }

  getGeometryById(id: number) {
    return this.http.get<ServerGeo>(`${environment.apiUrl}geometrias/${id}`);
  }

  addGeometry(body: any) {
    return this.http.post(`${environment.apiUrl}geometrias`, body, {
      headers: {
        'has-token': '',
      },
    });
  }

  updateGeometry(geometryId: number, body: any) {
    return this.http.put(
      `${environment.apiUrl}geometrias/${geometryId}`,
      body,
      {
        headers: {
          'has-token': '',
        },
      }
    );
  }

  setGeometryValues(geometryValues: GeometryForm | GeometryForm[]) {
    return this.http.post(`${environment.apiUrl}valores`, geometryValues, {
      headers: {
        'has-token': '',
      },
    });
  }

  uploadPhoto({
    id,
    formData,
    fileId,
  }: {
    id: string;
    formData: FormData;
    fileId: number;
  }) {
    return this.http
      .post(
        `${environment.apiUrl}arquivos/${+id}/files/upload?tipo=1`,
        formData,
        {
          reportProgress: true,
          observe: 'events',
          headers: {
            'has-token': '',
          },
        }
      )
      .pipe(
        map((event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              return { status: 'progress', progress: 0, fileId };
            case HttpEventType.UploadProgress:
              const progress = Math.round(
                (100 * event.loaded) / (event.total || 1)
              );
              return { status: 'progress', progress, fileId };
            case HttpEventType.Response:
              return {
                status: 'done',
                body: event.body,
                fileId,
                progress: 100,
              };
            default:
              return null;
          }
        }),
        filter<HttpProgressReturn>(Boolean)
      );
  }

  removePhoto(photoId: number) {
    return this.http.delete(`${environment.apiUrl}arquivos/${photoId}`, {
      headers: {
        'has-token': '',
      },
    });
  }

  getLayerById(id: number) {
    if (id === null || id === undefined) {
      return of(null);
    }
    if (this.layerByIdCache.has(id)) {
      return of(this.layerByIdCache.get(id));
    }

    const pending = this.layerByIdPending.get(id);
    if (pending) {
      return pending.asObservable();
    }

    const subject = new ReplaySubject<any>(1);
    this.layerByIdPending.set(id, subject);
    if (this.isCriciuma) {
      this.queueLayerRequest(id);
    } else {
      if (!this.layerByIdBatching) {
        this.startLayerByIdBatching();
      }
      this.layerByIdRequests$.next(id);
    }
    return subject.asObservable();
  }

  getLayersByIds(ids: number[]) {
    const validIds = (ids || []).filter(
      (id) => typeof id === 'number' && !Number.isNaN(id)
    );
    if (!validIds.length) {
      return of([]);
    }
    const params = new HttpParams()
      .set('id_camadas', validIds.join(','))
      .set('noForms', 'true');

    return this.http.get(`${environment.apiUrl}camadas`, { params });
  }

  getLayerByIdWithForms(id: number) {
    if (id === null || id === undefined) {
      return of(null);
    }
    const params = new HttpParams().set('id_camadas', id.toString());
    return this.http.get(`${environment.apiUrl}camadas`, { params }).pipe(
      map((response: any) => (Array.isArray(response) ? response[0] : response))
    );
  }

  getCamadaComFormularios(layerId: number) {
    return this.http.get(`${environment.apiUrl}camadas/${layerId}`).pipe(
      catchError(() => this.getLayerByIdWithForms(layerId))
    );
  }

  getLayersByRepresentation(representacao: string) {
    const params = new HttpParams().set('representacao', representacao);
    return this.http.get<Camada[]>(`${environment.apiUrl}camadas`, { params });
  }

  async getVectorTiles(url: string) {
    const { authToken, tnToken } = await firstValueFrom(
      this.authFacade.publicTokens$.pipe(
        filter(
          (tokens): tokens is PublicTokensState =>
            !!tokens?.authToken && !!tokens?.tnToken
        )
      )
    );

    const headers = new HttpHeaders()
      .set('X-AUTH-TOKEN', authToken)
      .set('X-TN-TOKEN', tnToken);

    const data = await firstValueFrom(
      this.http.get(url, {
        responseType: 'arraybuffer',
        headers,
      })
    );
    return data;
  }

  getVectorTilesNoToken(url: string) {
    return firstValueFrom(
      this.http.get(url, {
        responseType: 'arraybuffer',
        headers: new HttpHeaders().set('no-token', ''),
      })
    );
  }

  private resolveLayerRequest(id: number, layer: any) {
    if (layer) {
      this.layerByIdCache.set(id, layer);
    }
    const subject = this.layerByIdPending.get(id);
    if (subject) {
      subject.next(layer);
      subject.complete();
      this.layerByIdPending.delete(id);
    }
  }

  private failLayerRequest(id: number, error: any) {
    const subject = this.layerByIdPending.get(id);
    if (subject) {
      subject.error(error);
      this.layerByIdPending.delete(id);
    }
  }

  getLotValuation(lotInscricao: string) {
    return this.getIcadApiAddrWithFallback().pipe(
      switchMap((icadAPIAddr: string) =>
        this.http.get(`${icadAPIAddr}imoveis/itbi/${lotInscricao}/`)
      )
    );
  }

  getPropertyResume(inscricao: string, page?: number, pageSize?: number) {
    return this.getIcadApiAddrWithFallback().pipe(
      withLatestFrom(this.authFacade.publicTokens$),
      switchMap(([icadAPIAddr, publicTokens]) => {
        const headers = {
          'no-token': '',
          'X-AUTH-TOKEN': publicTokens?.authToken as string,
          'X-TN-TOKEN': publicTokens?.tnToken as string,
        };

        let params = new HttpParams().set('inscricao', inscricao);
        if (page != null && pageSize != null) {
          params = params.set('page', page).set('page_size', pageSize);
        }

        return this.http.get(`${icadAPIAddr}imoveis/match_resumido`, {
          headers,
          params,
          observe: 'response',
        });
      })
    );
  }

  getLotValuationByIds(ids: string) {
    return this.getIcadApiAddrWithFallback().pipe(
      switchMap((icadAPIAddr: string) =>
        this.http.get(`${icadAPIAddr}imoveis/itbi/ids/?array=${ids}`)
      )
    );
  }

  extractImoveisList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.results)) {
      return response.results;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    return [];
  }

  private parseTotalCountHeader(headers: {
    get: (name: string) => string | null;
  }): number | null {
    const raw =
      headers.get('X-Total-Count') ||
      headers.get('x-total-count') ||
      headers.get('X-TOTAL-COUNT');
    if (raw == null || raw === '') {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Identidade de uma unidade, usada para deduplicar páginas e para detectar
   * uma API que ignora os parâmetros de paginação e devolve sempre a mesma
   * página.
   */
  private imovelIdentity(imovel: any): string {
    const id =
      imovel?.idIntegracao ??
      imovel?.id_integracao ??
      imovel?.id ??
      imovel?.['inscrição_imobiliária'] ??
      imovel?.inscricao_imobiliaria ??
      imovel?.inscricao;
    return id != null && id !== '' ? String(id) : JSON.stringify(imovel ?? {});
  }

  /**
   * Percorre todas as páginas de um endpoint de unidades.
   *
   * Encerra quando a API sinaliza fim (`next` ausente, `page >= total_pages`,
   * total atingido, página vazia ou página incompleta) e trata "página além da
   * última" (404) como fim de lista. Atingir o teto de segurança, ou coletar
   * menos itens do que o total informado, é falha — nunca lista parcial
   * silenciosa.
   */
  private async collectAllPages(
    fetchPage: (
      page: number,
      pageSize: number
    ) => Promise<{ body: any; totalCountHeader: number | null }>,
    pageSize: number,
    maxPages = 200
  ): Promise<{ imoveis: any[]; totalCount: number | null }> {
    const allImoveis: any[] = [];
    const seen = new Set<string>();
    let page = 1;
    let totalCount: number | null = null;
    let previousPageIdentities: string | null = null;

    while (true) {
      let body: any;
      let totalCountHeader: number | null = null;

      try {
        const result = await fetchPage(page, pageSize);
        body = result.body;
        totalCountHeader = result.totalCountHeader;
      } catch (error: any) {
        // Total múltiplo exato do tamanho da página: a página seguinte à última
        // não existe. Isso é fim de lista, não erro.
        if (page > 1 && error?.status === 404) {
          break;
        }
        throw error;
      }

      const pageResults = this.extractImoveisList(body);
      const bodyCount =
        typeof body?.count === 'number' ? body.count : null;
      const pageTotalCount = bodyCount ?? totalCountHeader;
      if (pageTotalCount != null) {
        totalCount = pageTotalCount;
      }

      if (pageResults.length === 0) {
        break;
      }

      // API que ignora `page`/`page_size` devolve sempre o mesmo conjunto.
      const pageIdentities = pageResults
        .map((imovel) => this.imovelIdentity(imovel))
        .join('|');
      if (previousPageIdentities === pageIdentities) {
        break;
      }
      previousPageIdentities = pageIdentities;

      for (const imovel of pageResults) {
        const identity = this.imovelIdentity(imovel);
        if (seen.has(identity)) {
          continue;
        }
        seen.add(identity);
        allImoveis.push(imovel);
      }

      const totalPages =
        typeof body?.total_pages === 'number' ? body.total_pages : null;
      const hasPagingEnvelope =
        body != null && !Array.isArray(body) && 'next' in body;

      const reachedTotal = totalCount != null && allImoveis.length >= totalCount;
      const reachedLastPage = totalPages != null && page >= totalPages;
      const noNextLink = hasPagingEnvelope && !body.next;
      const shortPage =
        !hasPagingEnvelope && totalPages == null && pageResults.length < pageSize;

      if (reachedTotal || reachedLastPage || noNextLink || shortPage) {
        break;
      }

      page += 1;

      if (page > maxPages) {
        throw new Error(
          `Paginação de unidades excedeu ${maxPages} páginas — carregamento abortado.`
        );
      }
    }

    if (totalCount != null && allImoveis.length < totalCount) {
      throw new Error(
        `Carregamento incompleto de unidades: ${allImoveis.length} de ${totalCount}.`
      );
    }

    return { imoveis: allImoveis, totalCount };
  }

  async getAllPropertyResumeByInscricao(
    inscricao: string,
    pageSize = 50
  ): Promise<{ imoveis: any[]; totalCount: number | null }> {
    return this.collectAllPages(async (page, size) => {
      const response = await firstValueFrom(
        this.getPropertyResume(inscricao, page, size)
      );
      return {
        body: response.body,
        totalCountHeader: this.parseTotalCountHeader(response.headers),
      };
    }, pageSize);
  }

  getImoveisByInscLotePage(inscLote: string, page = 1, pageSize = 50) {
    return this.getIcadApiAddrWithFallback().pipe(
      switchMap((icadAPIAddr: string) => {
        const params = new HttpParams()
          .set('insc_lote', inscLote)
          .set('page', page)
          .set('page_size', pageSize);
        return this.http.get<any>(`${icadAPIAddr}imoveis/`, { params });
      })
    );
  }

  async getAllImoveisByInscLote(
    inscLote: string,
    pageSize = 50
  ): Promise<any[]> {
    const { imoveis } = await this.collectAllPages(async (page, size) => {
      const body = await firstValueFrom(
        this.getImoveisByInscLotePage(inscLote, page, size)
      );
      return { body, totalCountHeader: null };
    }, pageSize);
    return imoveis;
  }

  private getIcadApiAddrWithFallback() {
    return this.projectsFacade.icadAPIAddr$.pipe(
      switchMap((icadAPIAddr: string | null) => {
        if (icadAPIAddr) {
          return of(icadAPIAddr);
        }
        const icadApiAddr$ = this.http
          .get<any[]>(
            `${environment.apiUrl}projetos/listar?ativo=TODOS&noForms=true&noLayers=true&noExtras=true&tipoProjeto=ICAD`
          )
          .pipe(
            map((projects) => projects?.[0]?.enderecoApi as string),
            filter((addr): addr is string => !!addr)
          );
        if (!this.isCriciuma) {
          return icadApiAddr$;
        }
        // Criciúma: a lista de projetos era pedida de novo a cada lote aberto (~0,3 s em série)
        if (this.icadApiAddr) {
          return of(this.icadApiAddr);
        }
        return icadApiAddr$.pipe(tap((addr) => (this.icadApiAddr = addr)));
      })
    );
  }

  getAllVm2s(featureId: number) {
    return this.projectsFacade.city$.pipe(
      switchMap((city: string) => {
        const isCriciuma = city && city === 'CRICIÚMA';

        let url = `${environment.apiUrl}${isCriciuma ? 'criciuma' : 'rincao'
          }/logradouros/lote?id_geom=${featureId}`;

        if (isCriciuma) {
          return this.http.get(url);
        }
        return this.http.get(url);
      })
    );
  }

  getBrusqueVM2(geom: string) {
    // geom as
    return this.http.post(
      `${environment.apiUrl}brusque/logradouros/lote?idPotencialConstrutivo=9340&idPVG=33004`,
      {
        geom,
      }
    );
  }

  getComercialFactor({
    idTiff,
    lat,
    lng,
  }: {
    idTiff: number;
    lat: number;
    lng: number;
  }) {
    return this.http
      .get(`${environment.apiUrl}valorPixel/${idTiff}?x=${lng}&y=${lat}`)
      .pipe(map(({ valor }: any) => valor));
  }

  onComercialFactor(comercialFactor: number) {
    this.comercialFactorSource$.next(comercialFactor);
  }

  onCentroidLoteWKB(centroidLoteWKB: string | ArrayBuffer) {
    this.centroidLoteWKBSource$.next(centroidLoteWKB);
  }

  getZoneInfo({ lng, lat }: any) {
    return this.projectsFacade.zoneId$.pipe(
      switchMap((zoneId: number | null) => {
        if (!zoneId) {
          return EMPTY;
        }
        return this.http.get(
          `${environment.apiUrl}valores/projeto/${zoneId}?x=${lng}&y=${lat}`
        );
      })
    );
  }

  async getDataFromBuscaImoveisAll(
    camada: 'lote' | 'imobiliario' = 'lote',
    id: any
  ) {
    const { cidade } = await firstValueFrom(this.systemFacade.tenantConfig$);
    const url = `${environment.apiDjangoDirectUrl}${cidade}/${camada}/busca_imoveis_all/${id}/`;

    const data = await this.http.get(url, {
      headers: new HttpHeaders()
        .set('no-token', '')
        .append('Authorization', environment.djangoDirectUrlToken),
    });

    const resData = await firstValueFrom(data.pipe(take(1)));

    return resData;
  }

  async getAllExternalData(idCamada: any, idLote: any) {
    const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    const { cidade } = tenantConfig;

    const data = await this.http.get(
      `${environment.apiDjangoDirectUrl}${cidade}/${idCamada}/busca_imoveis_all/${idLote}`,
      {
        headers: new HttpHeaders()
          .set('no-token', '')
          .append('Authorization', environment.djangoDirectUrlToken),
      }
    );

    const resData = await firstValueFrom(data.pipe(take(1)));

    return resData;
  }

  async getUnidadesByLote(inscricao) {
    const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    const tenant = tenantConfig?.cidade as string;

    const imobiliarioKey = this.getImobiliatioKey(tenantConfig);

    const result = await postgrestClient<any>(tenant)
      .from(imobiliarioKey)
      .select('*')
      .eq('id_lote', inscricao);

    return result.data;
  }

  async getIndiceReducao(geomWKB: any) {
    const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    const publicTokens = await firstValueFrom(this.authFacade.publicTokens$);
    const tenant = tenantConfig?.cidade as string;

    const postgrestUrl = `${environment.apiGatewayUrl}${
      (environment as any).apiGatewayRestPrefix ?? 'rest/'
    }rpc/indice_de_reducao?geom_lote=${geomWKB}`;
    const result: any = await firstValueFrom(this.http.get(postgrestUrl, {
      headers: new HttpHeaders()
        .set('no-token', '')
        .append('Authorization', `Bearer ${publicTokens?.authToken}`),
    }));
    // const result = await postgrestClient<any>(tenant).rpc('indice_de_reducao', {
    //   id_lote: idLote,
    // });

    // const result = await postgrestClient<any>(tenant)
    //   .rpc('indice_de_reducao')
    //   .eq('id_lote', idLote);

    // return result.data;
    return result;
  }

  async getFatorLocalizacao(geomWKB: any, url?: string) {
    // const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    // const tenant = tenantConfig?.cidade as string;

    // // https://blumenau.postgrest.geo360.com.br/rpc/get_raster_pixel?centroid_geom=0101000000abcfd556ec8748c0f54a598638d63ac0
    // // const result = await postgrestClient<any>(tenant).rpc('get_raster_pixel', {
    // //   centroid_geom: geomWKB,
    // // });
    const currentUrl = url || environment.apiGatewayUrl;
    const restPrefix = url
      ? 'rest/'
      : (environment as any).apiGatewayRestPrefix ?? 'rest/';
    const postgrestUrl = `${currentUrl}${restPrefix}rpc/get_raster_pixel?centroid_geom=${geomWKB}`;
    const publicTokens = await firstValueFrom(this.authFacade.publicTokens$);

    let result: any = undefined;
    let result2: any = undefined;
    let error1: any = null;
    let error2: any = null;
    let result1Success = false;
    let result2Success = false;

    try {
      result = await firstValueFrom(this.http.get(postgrestUrl, {
        headers: new HttpHeaders()
          .set('no-token', '')
          .append('Authorization', `Bearer ${publicTokens?.authToken}`),
      }));
      result1Success = true;
    } catch (e) {
      error1 = e;
    }

    try {
      result2 = await firstValueFrom(this.http.get(postgrestUrl, {
        headers: new HttpHeaders()
          .set('no-token', '')
          .append('x-auth-token', publicTokens?.authToken as string)
          .append('x-tn-token', publicTokens?.tnToken as string)
      }));
      result2Success = true;
    } catch (e) {
      error2 = e;
    }

    if (result1Success) {
      return result;
    }

    if (result2Success) {
      return result2;
    }

    // Both requests failed, throw the first error
    throw error1 || error2;
  }

  async getLoteById(idLote: any) {
    const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    const tenant = tenantConfig?.cidade as string;

    const loteKey = this.getImobiliatioKey(tenantConfig);

    const result = await postgrestClient(tenant)
      .from(loteKey)
      .select('geom::text')
      .eq('id', idLote);

    return result.data as any[];
  }

  async getImovelById(idImovel: any) {
    const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    const tenant = tenantConfig?.cidade as string;

    const imobiliarioKey = this.getImobiliatioKey(tenantConfig);

    const result = await postgrestClient(tenant)
      .from(imobiliarioKey)
      .select('*')
      .eq('id', idImovel);

    return result.data as any[];
  }

  async getLoteInfo(idLote: any) {
    const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    const tenant = tenantConfig?.cidade as string;

    const result = await postgrestClient(tenant)
      .from('lotes_imb_4326')
      .select('*')
      .eq('id_lote', idLote);

    return result.data as any[];
  }

  async getImoveisByPessoaId(idPessoa: any) {
    const tenantConfig = await firstValueFrom(this.systemFacade.tenantConfig$);
    const tenant = tenantConfig?.cidade as string;

    const result = await postgrestClient(tenant)
      .from('imobiliario_pessoa')
      .select('*')
      .eq('id_pessoa', idPessoa);

    return result.data as any[];
  }

  async getValuationLayers() {
    let pvgeoId = await firstValueFrom(this.projectsFacade.pvgeoId$);

    if (!pvgeoId) {
      pvgeoId = await firstValueFrom(this.projectsFacade.currentPvgeoId$);
    }

    const publicTokens = await firstValueFrom(this.authFacade.publicTokens$);
    const user = await firstValueFrom(this.authFacade.user$);
    const authToken = user?.authToken ?? publicTokens?.authToken ?? '';

    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${authToken}`)
      .set('no-token', '');

    const url = `${environment.apiDjangoDirectUrl}portal/projetos/${pvgeoId}/camadas/modelagem`;
    const result = await firstValueFrom(
      this.http.get<any[]>(url, { headers })
    );
    return result ?? [];
  }

  private valuationInfoDEFAULTSource = new BehaviorSubject<any>(null);
  valuationInfoDEFAULT$ = this.valuationInfoDEFAULTSource.asObservable();

  setValuationInfoDEFAULT(valuationInfo) {
    this.valuationInfoDEFAULTSource.next(valuationInfo);
  }

  clearValuationInfoDEFAULT() {
    this.valuationInfoDEFAULTSource.next(null);
  }

  getIntersectedGeoms(layers: any[], wkt: string) {
    const layersIds = layers.map((layer) => layer.id);
    const ids = layersIds.reduce((acc, curr) => {
      if (!acc.length) return `${curr}`;
      acc = `${acc},${curr}`;
      return acc;
    }, '');

    const params = new HttpParams().append('ids', ids).append('wkt', wkt);

    return this.http.get(`${environment.apiUrl}geometrias/intersect-geom`, {
      params,
    });
  }

  genericValuesById(ids: number[]) {
    return this.http.post<any[]>(
      `${environment.apiDjangoUrl}postgrest/tipos/valores/geometrias/`,
      { ids_geom: ids }
    );
  }

  async getValuationStructure(inscricao) {
    const params = new HttpParams()
      .append('id_projetos', '1226')
      .append('id_tenant', '774');
    return firstValueFrom(
      this.http.get(
        `${environment.apiDjangoUrl}crud/municipio/observatorio/avaliacao/${inscricao}/`,
        { params }
      )
    );
  }
}
